'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Mission {
  id: number;
  title: string;
  client: string;
  category: string;
  image: string;
  alt: string;
  challenge: string;
  solution: string;
  results: string[];
  tags: string[];
}

const MissionGallery = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = ['All', 'Space Systems', 'Aerospace', 'Defense', 'Commercial'];

  const missions: Mission[] = [
  {
    id: 1,
    title: 'Next-Gen Satellite Deployment System',
    client: 'Leading Space Technology Company',
    category: 'Space Systems',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1705ecf51-1783178462464.png",
    alt: 'Advanced satellite deployment mechanism with precision-engineered titanium components in orbital configuration',
    challenge: 'Design and manufacture a lightweight, high-reliability deployment mechanism for a constellation of 50+ satellites with zero-failure tolerance.',
    solution: 'Implemented advanced titanium alloy CNC machining with integrated testing protocols, achieving 40% weight reduction while exceeding strength requirements.',
    results: ['100% deployment success rate', '40% weight reduction', '6-month accelerated timeline'],
    tags: ['Titanium Alloy', 'CNC Machining', 'AS9100', 'Mission Critical']
  },
  {
    id: 2,
    title: 'Hypersonic Vehicle Components',
    client: 'Defense Aerospace Contractor',
    category: 'Defense',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4e0af37-1783178463753.png",
    alt: 'Heat-resistant aerospace components designed for hypersonic flight with specialized thermal protection coatings',
    challenge: 'Manufacture thermal protection components capable of withstanding Mach 5+ speeds with extreme temperature gradients.',
    solution: 'Developed proprietary composite layup process with advanced thermal barrier coatings, validated through extensive testing.',
    results: ['Mach 5.5 capability verified', 'Temperature resistance 3000°F+', 'ITAR compliant production'],
    tags: ['Composites', 'Thermal Protection', 'ITAR', 'Defense Grade']
  },
  {
    id: 3,
    title: 'Commercial Launch Vehicle Structures',
    client: 'NewSpace Launch Provider',
    category: 'Aerospace',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_195602761-1766493774304.png",
    alt: 'Large-scale rocket structural components with precision-welded aluminum alloy sections in assembly facility',
    challenge: 'Rapid production of large-scale structural components for commercial launch vehicle with aggressive timeline and cost targets.',
    solution: 'Implemented automated sheet metal fabrication with real-time quality monitoring, reducing production time by 60%.',
    results: ['60% faster production', '30% cost reduction', '12 successful launches'],
    tags: ['Sheet Metal', 'Automation', 'Cost Optimization', 'High Volume']
  }];


  const filteredMissions = selectedCategory === 'All' ?
  missions :
  missions.filter((mission) => mission.category === selectedCategory);

  const handleCategoryChange = (category: string) => {
    if (!isHydrated) return;
    setSelectedCategory(category);
  };

  return (
    <section className="relative py-24 bg-gradient-to-b from-primary to-background">
      <div className="mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-mission-orange/10 rounded-full border border-mission-orange/30 mb-6">
            <Icon name="RocketLaunchIcon" size={20} className="text-mission-orange" />
            <span className="font-cta text-sm text-mission-orange">Mission Success Stories</span>
          </div>
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Proven Excellence in Action
          </h2>
          <p className="font-body text-lg text-technical-gray">
            Real aerospace challenges solved with precision engineering and mission-critical reliability.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) =>
          <button
            key={category}
            onClick={() => handleCategoryChange(category)}
            disabled={!isHydrated}
            className={`px-6 py-2.5 rounded-lg font-cta text-sm transition-all duration-300 ${
            selectedCategory === category ?
            'bg-accent text-primary shadow-glow' :
            'bg-muted text-technical-gray hover:bg-accent/20 hover:text-accent border border-border'}`
            }>

              {category}
            </button>
          )}
        </div>

        {/* Missions Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {filteredMissions.map((mission) =>
          <div
            key={mission.id}
            className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-glow">

              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <AppImage
                src={mission.image}
                alt={mission.alt}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/60 to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4 px-3 py-1 bg-accent/20 backdrop-blur-sm rounded-full border border-accent/30">
                  <span className="font-cta text-xs text-accent">{mission.category}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <h3 className="font-headline text-xl font-bold text-foreground group-hover:text-accent transition-colors duration-300">
                  {mission.title}
                </h3>
                
                <p className="font-body text-sm text-technical-gray italic">
                  {mission.client}
                </p>

                <div className="space-y-3">
                  <div>
                    <p className="font-cta text-xs text-accent uppercase tracking-wider mb-1">Challenge</p>
                    <p className="font-body text-sm text-technical-gray line-clamp-2">
                      {mission.challenge}
                    </p>
                  </div>

                  <div>
                    <p className="font-cta text-xs text-accent uppercase tracking-wider mb-1">Results</p>
                    <ul className="space-y-1">
                      {mission.results.slice(0, 2).map((result, index) =>
                    <li key={index} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={16} className="text-aerospace-green mt-0.5 flex-shrink-0" />
                          <span className="font-body text-sm text-technical-gray">{result}</span>
                        </li>
                    )}
                    </ul>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {mission.tags.slice(0, 3).map((tag) =>
                <span
                  key={tag}
                  className="px-2 py-1 bg-muted rounded-md border border-border font-body text-xs text-technical-gray">

                      {tag}
                    </span>
                )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            href="/capabilities"
            className="inline-flex items-center space-x-3 px-8 py-4 bg-transparent border-2 border-accent text-accent font-cta text-base font-medium rounded-lg hover:bg-accent/10 transition-all duration-300">

            <span>VIEW ALL MISSION STORIES</span>
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>);

};

export default MissionGallery;